import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, fmtDay, INTERVAL_HU, Invoice, INVOICE_STATUS_CLASS, money, Recurring } from '../api';
import { RecurringForm } from '../components/RecurringForm';
import { useSession } from '../session';
import { ErrorBox, useAsync } from '../components/ui';

type Rec = { company_id: string; company_name: string; currency: string; invoices: number; current: number; d1_30: number; d31_60: number; d60_plus: number; total: number };
const TABS: [string, string][] = [['open', 'nyitott'], ['overdue', 'lejárt'], ['proforma', 'díjbekérők'], ['all', 'mind'], ['recurring', 'ismétlődő'], ['receivables', 'kintlévőség cégenként']];

/** Számlák és kintlévőség (M4). */
export function InvoicesPage() {
  const s = useSession();
  const nav = useNavigate();
  const [tab, setTab] = useState('open');
  const [q, setQ] = useState('');
  const [data, setData] = useState<{ items: Invoice[]; total: number; open_totals: { currency: string; open: number }[] } | null>(null);
  const [rec, setRec] = useState<Rec[] | null>(null);
  const [recs, setRecs] = useState<Recurring[] | null>(null);
  const [edit, setEdit] = useState<Recurring | null>(null);
  const [overdue, setOverdue] = useState<{ currency: string; open: number }[]>([]);
  const a = useAsync();
  useEffect(() => {
    const t = setTimeout(() => a.run(async () => {
      if (tab === 'recurring') { setRecs((await api<{ items: Recurring[] }>('/invoices/recurring')).items); return; }
      if (tab === 'receivables') { setRec((await api<{ items: Rec[] }>('/invoices/receivables')).items); return; }
      const qs = new URLSearchParams({ limit: '200', ...(q ? { query: q } : {}), ...(tab === 'open' ? { open: 'true' } : tab === 'overdue' ? { overdue: 'true' } : tab === 'proforma' ? { kind: 'proforma' } : {}) });
      setData(await api(`/invoices?${qs}`));
      setOverdue((await api<{ open_totals: { currency: string; open: number }[] }>('/invoices?overdue=true&limit=1')).open_totals);
    }), q ? 200 : 0);
    return () => clearTimeout(t);
  }, [tab, q]);
  return (
    <div>
      <div className="page-head">
        <h2>Számlák</h2>
        {!['receivables', 'recurring'].includes(tab) && <input className="search" placeholder="Keresés sorszám vagy cég szerint…" value={q} onChange={(e) => setQ(e.target.value)} />}
        {s.role === 'owner' && <Link to="/szamlak/beallitasok">Számlázó beállítása</Link>}
      </div>
      <div className="tiles">
        {(data?.open_totals ?? []).filter((x) => x.open > 0).map((x) => <div key={x.currency} className="tile"><b>{money(x.open, x.currency)}</b><span>nyitott ({x.currency})</span></div>)}
        {overdue.filter((x) => x.open > 0).map((x) => <div key={x.currency} className="tile bad"><b>{money(x.open, x.currency)}</b><span>lejárt</span></div>)}
      </div>
      <div className="filters">{TABS.map(([k, l]) => <button key={k} className={tab === k ? '' : 'secondary'} onClick={() => setTab(k)}>{l}</button>)}</div>
      <ErrorBox error={a.error} />
      {edit && <RecurringForm rec={edit} onClose={() => setEdit(null)} onSaved={(r) => { setRecs((recs ?? []).map((x) => (x.id === r.id ? r : x))); setEdit(null); }} />}
      {tab === 'recurring' ? (
        <>
          <p className="muted small">Az esedékes ismétlődő számlákat az ütemező minden reggel 6 óra után állítja ki. Újat egy kiállított számla oldalán, az „Ismétlődővé tétel” gombbal vehetsz fel.</p>
          <table className="list">
            <thead><tr><th>Megnevezés</th><th>Cég</th><th>Gyakoriság</th><th className="num">Nettó</th><th>Következő</th><th>Vége</th><th className="num">Kiállítva</th><th>Állapot</th></tr></thead>
            <tbody>
              {recs?.map((r) => (
                <tr key={r.id} onClick={() => (s.role === 'owner' || s.role === 'finance') && setEdit(r)}>
                  <td>{r.title}{r.kind === 'proforma' && <span className="muted"> (díjbekérő)</span>}</td>
                  <td><Link to={`/cegek/${r.company_id}`} onClick={(e) => e.stopPropagation()}>{r.company_name}</Link></td>
                  <td>{INTERVAL_HU[r.interval_months]}</td><td className="num">{money(r.net_total, r.currency)}</td>
                  <td>{r.active ? fmtDay(r.next_date) : '–'}</td><td>{r.end_date ? fmtDay(r.end_date) : '–'}</td>
                  <td className="num">{r.last_invoice_id ? <Link to={`/szamlak/${r.last_invoice_id}`} onClick={(e) => e.stopPropagation()}>{r.issued_count}</Link> : r.issued_count}</td>
                  <td>{r.active ? <span className="badge">aktív</span> : <span className="badge muted">leállítva</span>}{r.last_error && <span className="badge badge-risk-external" title={r.last_error}>hiba</span>}</td>
                </tr>
              ))}
              {recs?.length === 0 && <tr><td colSpan={8} className="muted">Nincs ismétlődő számla.</td></tr>}
            </tbody>
          </table>
        </>
      ) : tab === 'receivables' ? (
        <table className="list">
          <thead><tr><th>Cég</th><th className="num">Számla</th><th className="num">Nem lejárt</th><th className="num">1–30 nap</th><th className="num">31–60 nap</th><th className="num">60+ nap</th><th className="num">Összesen</th></tr></thead>
          <tbody>
            {rec?.map((r) => (
              <tr key={r.company_id + r.currency} onClick={() => nav(`/cegek/${r.company_id}`)}>
                <td><Link to={`/cegek/${r.company_id}`}>{r.company_name}</Link></td><td className="num">{r.invoices}</td>
                <td className="num">{money(r.current, r.currency)}</td><td className={`num ${r.d1_30 ? 'overdue' : ''}`}>{money(r.d1_30, r.currency)}</td>
                <td className={`num ${r.d31_60 ? 'overdue' : ''}`}>{money(r.d31_60, r.currency)}</td><td className={`num ${r.d60_plus ? 'overdue' : ''}`}>{money(r.d60_plus, r.currency)}</td>
                <td className="num"><b>{money(r.total, r.currency)}</b></td>
              </tr>
            ))}
            {rec?.length === 0 && <tr><td colSpan={7} className="muted">Nincs nyitott számla.</td></tr>}
          </tbody>
        </table>
      ) : (
        <table className="list">
          <thead><tr><th>Sorszám</th><th>Típus</th><th>Cég</th><th>Állapot</th><th className="num">Bruttó</th><th className="num">Nyitott</th><th>Határidő</th></tr></thead>
          <tbody>
            {data?.items.map((i) => (
              <tr key={i.id} onClick={() => nav(`/szamlak/${i.id}`)}>
                <td><Link to={`/szamlak/${i.id}`}>{i.number ?? '(kiállítás alatt)'}</Link></td><td>{i.kind_hu}</td><td>{i.company_name}</td>
                <td><span className={`badge ${INVOICE_STATUS_CLASS[i.status] ?? ''}`}>{i.status_hu}</span>{i.overdue && <span className="badge badge-risk-external">{i.days_late} napja lejárt</span>}</td>
                <td className="num">{money(i.gross_total, i.currency)}</td><td className="num">{i.open_amount ? money(i.open_amount, i.currency) : '–'}</td>
                <td className={i.overdue ? 'overdue' : ''}>{fmtDay(i.due_date)}</td>
              </tr>
            ))}
            {data?.items.length === 0 && <tr><td colSpan={7} className="muted">Nincs ilyen számla. Számlát elfogadott ajánlatból vagy a cég adatlapjáról állíthatsz ki.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}
